import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  X, Tag, Filter, Activity, Sliders, RotateCcw, Check, Plus, Search,
  AlertTriangle, Trash2, Loader2, Save, ChevronDown, ChevronRight, Info,
} from 'lucide-react'; // eslint-disable-line no-unused-vars
import {
  getAprovacoesMapping,
  getAprovacoesMappingDistintos,
  putAprovacaoFiltros,
  deleteAprovacaoFiltros,
  putAprovacaoStatus,
  deleteAprovacaoStatus,
  putAprovacaoOps,
  deleteAprovacaoOps,
  resetAprovacaoMapping,
  setAprovacaoThreshold,
  deleteAprovacaoThreshold,
} from '../api';

const monoFont = `'JetBrains Mono', 'SF Mono', ui-monospace, Menlo, monospace`;

const COMPANY_THEME = {
  'jmv':     { color: '#60a5fa', label: 'JMV' },
  'fix-csp': { color: '#a78bfa', label: 'FIX CSP' },
  'fix-msc': { color: '#f472b6', label: 'FIX MSC' },
  'vip':     { color: '#34d399', label: 'VIP' },
};

const SECTION_COLOR = {
  filtros: '#f472b6',
  status:  '#fbbf24',
  regras:  '#60a5fa',
};

const normalizeForCompare = (s) =>
  String(s || '').trim().toUpperCase().replace(/\s+/g, ' ');

const setEquals = (a, b) => {
  if (a.length !== b.length) return false;
  const sa = new Set(a.map(normalizeForCompare));
  for (const v of b) if (!sa.has(normalizeForCompare(v))) return false;
  return true;
};

// =================== ATOMS ===================

function Pill({ children, color = '#64748b', bg, border, style }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 9px', borderRadius: 999,
      fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em',
      background: bg || `${color}14`,
      border: `1px solid ${border || color + '33'}`,
      color, fontFamily: monoFont,
      ...style,
    }}>{children}</span>
  );
}

function EditedBadge() {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 8px', borderRadius: 999,
      fontSize: 9.5, fontWeight: 800, letterSpacing: '0.1em',
      background: 'rgba(251,191,36,0.12)',
      border: '1px solid rgba(251,191,36,0.45)',
      color: '#b45309', fontFamily: monoFont, textTransform: 'uppercase',
    }}>
      <AlertTriangle size={10} />
      Editado
    </span>
  );
}

function SectionHeader({ Icone, color, titulo, subtitulo, right, isEdited }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '14px 16px', borderBottom: '1px solid rgba(15,23,42,0.08)',
      background: `linear-gradient(180deg, ${color}10 0%, transparent 100%)`,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: `linear-gradient(135deg, ${color}28 0%, ${color}08 100%)`,
        border: `1px solid ${color}40`,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icone size={17} color={color} strokeWidth={2.2} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <h3 style={{ fontSize: 14, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.01em' }}>{titulo}</h3>
          {isEdited && <EditedBadge />}
        </div>
        {subtitulo && (
          <p style={{ fontSize: 11, color: '#475569', marginTop: 2, lineHeight: 1.4 }}>{subtitulo}</p>
        )}
      </div>
      {right}
    </div>
  );
}

function SmallBtn({ onClick, children, color = '#475569', disabled, title, danger, variant }) {
  const variants = {
    solid: {
      background: danger
        ? 'linear-gradient(180deg, #ef4444, #dc2626)'
        : 'linear-gradient(180deg, #10b981, #059669)',
      color: '#fff',
      border: '1px solid rgba(15,23,42,0.10)',
      boxShadow: danger ? '0 4px 12px rgba(239,68,68,0.3)' : '0 4px 12px rgba(16,185,129,0.3)',
    },
    ghost: {
      background: 'rgba(15,23,42,0.04)',
      color,
      border: '1px solid rgba(15,23,42,0.08)',
    },
    outline: {
      background: 'transparent',
      color: danger ? '#fca5a5' : color,
      border: `1px solid ${danger ? 'rgba(239,68,68,0.4)' : 'rgba(15,23,42,0.12)'}`,
    },
  };
  const v = variants[variant || 'ghost'];
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        ...v,
        padding: '7px 12px', borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontSize: 11.5, fontWeight: 700, letterSpacing: '0.02em',
        transition: 'all 0.15s',
        fontFamily: 'inherit',
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.filter = 'brightness(1.15)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.filter = 'none'; }}
    >
      {children}
    </button>
  );
}

function SearchInput({ value, onChange, placeholder }) {
  return (
    <div style={{
      position: 'relative',
      display: 'flex', alignItems: 'center',
    }}>
      <Search size={12} color="#64748b" style={{ position: 'absolute', left: 10, pointerEvents: 'none' }} />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          flex: 1, padding: '7px 10px 7px 28px',
          borderRadius: 8, border: '1px solid #e2e8f0',
          background: '#f8fafc', color: '#1e293b',
          fontSize: 11.5, outline: 'none', fontFamily: 'inherit',
          width: '100%',
        }}
        onFocus={(e) => { e.target.style.borderColor = '#cbd5e1'; }}
        onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; }}
      />
    </div>
  );
}

function Chip({ label, onRemove, color = '#15803d', bg = 'rgba(16,185,129,0.12)', border = 'rgba(16,185,129,0.35)', warn }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '5px 4px 5px 10px', borderRadius: 999,
      background: warn ? 'rgba(251,191,36,0.10)' : bg,
      border: `1px solid ${warn ? 'rgba(251,191,36,0.35)' : border}`,
      fontSize: 11, fontWeight: 700, color: warn ? '#b45309' : color,
      fontFamily: monoFont, letterSpacing: '0.02em',
      maxWidth: '100%',
    }}>
      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
      {onRemove && (
        <button
          onClick={onRemove}
          aria-label="Remover"
          style={{
            width: 18, height: 18, borderRadius: '50%',
            background: 'rgba(15,23,42,0.04)', border: 'none',
            cursor: 'pointer', display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center', color: 'inherit', flexShrink: 0,
          }}
        ><X size={11} /></button>
      )}
    </span>
  );
}

// =================== FILTROS EDITOR ===================

function FiltrosEditor({
  empresaSlug, abaKey, abaConfig, filterField, matchType,
  distintos, distintosLoading,
  onChanged, isMobile,
}) {
  const defaultFiltros = abaConfig.default_filtros || [];
  const effectiveFiltros = abaConfig.effective_filtros || defaultFiltros;
  const isOverridden = !!abaConfig.filtros_is_overridden;

  const [draft, setDraft] = useState(effectiveFiltros);
  const [searchIn, setSearchIn] = useState('');
  const [searchOut, setSearchOut] = useState('');
  const [manualValue, setManualValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setDraft(effectiveFiltros);
  }, [effectiveFiltros.join('|')]); // eslint-disable-line

  const colKey = filterField === 'aparelho' ? 'aparelho' : 'tipo_servico';
  const allDistintos = distintos?.[colKey] || [];

  const draftSet = useMemo(() => new Set(draft.map(normalizeForCompare)), [draft]);

  const incluidosFiltrados = useMemo(() => {
    const q = normalizeForCompare(searchIn);
    if (!q) return draft;
    return draft.filter(v => normalizeForCompare(v).includes(q));
  }, [draft, searchIn]);

  const disponiveisFiltrados = useMemo(() => {
    const q = normalizeForCompare(searchOut);
    return allDistintos
      .filter(v => !draftSet.has(normalizeForCompare(v)))
      .filter(v => !q || normalizeForCompare(v).includes(q));
  }, [allDistintos, draftSet, searchOut]);

  const isDirty = !setEquals(draft, effectiveFiltros);
  const matchesDefault = setEquals(draft, defaultFiltros);
  const canSave = isDirty && draft.length > 0;

  const addValue = (v) => {
    const t = String(v || '').trim();
    if (!t) return;
    const norm = normalizeForCompare(t);
    if (draft.some(x => normalizeForCompare(x) === norm)) return;
    setDraft(prev => [...prev, t]);
  };
  const removeValue = (v) => {
    setDraft(prev => prev.filter(x => x !== v));
  };
  const addManual = () => {
    const t = manualValue.trim();
    if (!t) return;
    addValue(t);
    setManualValue('');
  };

  const onSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const r = await putAprovacaoFiltros(empresaSlug, abaKey, draft);
      await onChanged?.(r.data?.effective_filtros);
    } catch (e) {
      alert('Erro ao salvar: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const onReset = async () => {
    if (!window.confirm('Voltar para os filtros padrão dessa aba?')) return;
    setSaving(true);
    try {
      await deleteAprovacaoFiltros(empresaSlug, abaKey);
      await onChanged?.(defaultFiltros);
    } catch (e) {
      alert('Erro ao resetar: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const fieldLabel = filterField === 'aparelho' ? 'aparelho' : 'tipo_servico';
  const matchLabel = matchType === 'exact' ? 'exato' : 'contém';
  const matchTooltip = matchType === 'exact'
    ? 'Exato: o valor do registro precisa ser EXATAMENTE igual ao filtro (case-insensitive).'
    : 'Contém: cada filtro é uma substring procurada no valor do registro.';

  return (
    <div style={{
      borderRadius: 12,
      border: isOverridden
        ? '1px solid rgba(251,191,36,0.30)'
        : '1px solid rgba(244,114,182,0.18)',
      background: 'linear-gradient(180deg, rgba(244,114,182,0.04) 0%, rgba(255,255,255,0.7) 100%)',
      overflow: 'hidden',
      marginBottom: 12,
    }}>
      <button
        onClick={() => setExpanded(e => !e)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          padding: '12px 14px', cursor: 'pointer',
          background: 'transparent', border: 'none', color: 'inherit', textAlign: 'left',
        }}
      >
        {expanded ? <ChevronDown size={14} color="#475569" /> : <ChevronRight size={14} color="#475569" />}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>{abaConfig.titulo}</span>
            <Pill color="#6d28d9">{abaKey}</Pill>
            <Pill color="#1d4ed8">campo: {fieldLabel}</Pill>
            <span title={matchTooltip} style={{ cursor: 'help' }}>
              <Pill color="#b45309">match: {matchLabel}</Pill>
            </span>
            {isOverridden && <EditedBadge />}
          </div>
          <p style={{ fontSize: 10.5, color: '#64748b', marginTop: 3, fontFamily: monoFont }}>
            {draft.length} valor(es) incluído(s){isDirty ? ' · alterações não salvas' : ''}
          </p>
        </div>
      </button>

      {expanded && (
        <div style={{ padding: '0 14px 14px' }}>
          {abaConfig.filtros_updated_by && (
            <p style={{ fontSize: 10, color: '#64748b', marginBottom: 10, fontFamily: monoFont }}>
              Última edição: <span style={{ color: '#334155' }}>{abaConfig.filtros_updated_by}</span>
              {abaConfig.filtros_updated_at && ` em ${new Date(abaConfig.filtros_updated_at).toLocaleString('pt-BR')}`}
            </p>
          )}

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: 12,
          }}>
            {/* Incluídos */}
            <div style={{
              background: 'rgba(15,23,42,0.04)', borderRadius: 10,
              border: '1px solid rgba(15,23,42,0.04)', padding: 10,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Check size={13} color="#34d399" />
                <span style={{ fontSize: 11, fontWeight: 800, color: '#334155', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  Incluídos
                </span>
                <span style={{ fontSize: 10.5, color: '#64748b', fontFamily: monoFont, marginLeft: 'auto' }}>
                  {draft.length}
                </span>
              </div>
              <div style={{ marginBottom: 8 }}>
                <SearchInput value={searchIn} onChange={setSearchIn} placeholder="Filtrar incluídos…" />
              </div>
              <div style={{
                display: 'flex', flexWrap: 'wrap', gap: 6,
                maxHeight: 220, overflowY: 'auto', paddingRight: 4,
              }}>
                {incluidosFiltrados.length === 0 && (
                  <p style={{ fontSize: 11, color: '#64748b', padding: 8 }}>Nenhum valor.</p>
                )}
                {incluidosFiltrados.map(v => {
                  const norm = normalizeForCompare(v);
                  const inBase = allDistintos.some(d => normalizeForCompare(d) === norm);
                  return (
                    <Chip
                      key={v}
                      label={v}
                      warn={!inBase && allDistintos.length > 0}
                      onRemove={() => removeValue(v)}
                    />
                  );
                })}
              </div>
            </div>

            {/* Disponíveis */}
            <div style={{
              background: 'rgba(15,23,42,0.04)', borderRadius: 10,
              border: '1px solid rgba(15,23,42,0.04)', padding: 10,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Plus size={13} color="#60a5fa" />
                <span style={{ fontSize: 11, fontWeight: 800, color: '#334155', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  Disponíveis na base
                </span>
                <span style={{ fontSize: 10.5, color: '#64748b', fontFamily: monoFont, marginLeft: 'auto' }}>
                  {distintosLoading ? '…' : disponiveisFiltrados.length}
                </span>
              </div>
              <div style={{ marginBottom: 8 }}>
                <SearchInput value={searchOut} onChange={setSearchOut} placeholder="Buscar valores…" />
              </div>
              <div style={{
                display: 'flex', flexWrap: 'wrap', gap: 6,
                maxHeight: 220, overflowY: 'auto', paddingRight: 4,
              }}>
                {distintosLoading && (
                  <p style={{ fontSize: 11, color: '#64748b', padding: 8 }}>
                    <Loader2 size={12} style={{ animation: 'spin 1s linear infinite', display: 'inline', marginRight: 6 }} />
                    Carregando…
                  </p>
                )}
                {!distintosLoading && disponiveisFiltrados.length === 0 && (
                  <p style={{ fontSize: 11, color: '#64748b', padding: 8 }}>
                    Sem valores disponíveis. Use "Adicionar manual" abaixo.
                  </p>
                )}
                {!distintosLoading && disponiveisFiltrados.map(v => (
                  <button
                    key={v}
                    onClick={() => addValue(v)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '5px 10px', borderRadius: 999,
                      background: 'rgba(96,165,250,0.10)',
                      border: '1px solid rgba(96,165,250,0.25)',
                      color: '#1d4ed8', fontFamily: monoFont,
                      fontSize: 11, fontWeight: 700, cursor: 'pointer',
                      transition: 'all 0.12s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(96,165,250,0.20)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(96,165,250,0.10)'; }}
                  >
                    <Plus size={11} />
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 240 }}>{v}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Adicionar manual */}
          <div style={{
            marginTop: 10, padding: 10,
            background: 'rgba(15,23,42,0.035)', borderRadius: 10,
            border: '1px dashed rgba(15,23,42,0.10)',
            display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
          }}>
            <Plus size={13} color="#64748b" />
            <span style={{ fontSize: 11, fontWeight: 700, color: '#334155' }}>Adicionar valor manual:</span>
            <input
              value={manualValue}
              onChange={(e) => setManualValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addManual(); }}
              placeholder="Ex: FG PRE APROVADO XYZ"
              style={{
                flex: 1, minWidth: 180, padding: '7px 10px',
                borderRadius: 8, border: '1px solid #e2e8f0',
                background: '#f8fafc', color: '#1e293b',
                fontSize: 11.5, outline: 'none', fontFamily: monoFont,
              }}
            />
            <SmallBtn onClick={addManual} disabled={!manualValue.trim()} variant="ghost">
              <Plus size={12} /> Adicionar
            </SmallBtn>
          </div>

          {/* Rodapé sticky */}
          <div style={{
            marginTop: 12, paddingTop: 10,
            borderTop: '1px solid rgba(15,23,42,0.08)',
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          }}>
            {isOverridden && (
              <SmallBtn onClick={onReset} variant="outline" disabled={saving} title="Voltar ao padrão do sistema">
                <RotateCcw size={12} /> Resetar
              </SmallBtn>
            )}
            <span style={{ fontSize: 10.5, color: '#64748b', fontFamily: monoFont, marginLeft: 'auto' }}>
              {matchesDefault && isDirty && 'Salvar removerá o override (volta ao padrão)'}
              {!matchesDefault && isDirty && 'Alterações pendentes'}
              {!isDirty && 'Sem alterações'}
            </span>
            <SmallBtn
              onClick={onSave}
              disabled={!canSave || saving}
              variant="solid"
              title={draft.length === 0 ? 'Adicione ao menos um valor' : 'Salvar alterações'}
            >
              {saving ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={12} />}
              Salvar
            </SmallBtn>
          </div>
        </div>
      )}
    </div>
  );
}

// =================== STATUS EDITOR ===================

function StatusCheckboxGrid({
  title, Icone, accentColor, accentBg, accentBorder,
  allStatus, distintosLoading, draft, setDraft,
  selectedCountLabel, helperText,
}) {
  const [search, setSearch] = useState('');
  const [manual, setManual] = useState('');

  const draftSet = useMemo(() => new Set(draft.map(normalizeForCompare)), [draft]);

  // Union: status from distintos + manual additions not present in distintos,
  // so manual entries remain visible/toggle-able even if not in base.
  const allKnown = useMemo(() => {
    const seen = new Set(allStatus.map(normalizeForCompare));
    const extras = draft.filter(v => !seen.has(normalizeForCompare(v)));
    return [...allStatus, ...extras];
  }, [allStatus, draft]);

  const filtered = useMemo(() => {
    const q = normalizeForCompare(search);
    if (!q) return allKnown;
    return allKnown.filter(v => normalizeForCompare(v).includes(q));
  }, [allKnown, search]);

  const toggle = (v) => {
    const t = String(v || '').trim();
    if (!t) return;
    const norm = normalizeForCompare(t);
    if (draftSet.has(norm)) {
      setDraft(prev => prev.filter(x => normalizeForCompare(x) !== norm));
    } else {
      setDraft(prev => [...prev, t]);
    }
  };

  const addManual = () => {
    const t = manual.trim();
    if (!t) return;
    const norm = normalizeForCompare(t);
    if (!draftSet.has(norm)) {
      setDraft(prev => [...prev, t]);
    }
    setManual('');
  };

  const selectAllVisible = () => {
    setDraft(prev => {
      const cur = new Set(prev.map(normalizeForCompare));
      const next = [...prev];
      for (const v of filtered) {
        if (!cur.has(normalizeForCompare(v))) {
          next.push(v);
          cur.add(normalizeForCompare(v));
        }
      }
      return next;
    });
  };
  const clearAllVisible = () => {
    if (filtered.length === 0) return;
    const visibleSet = new Set(filtered.map(normalizeForCompare));
    setDraft(prev => prev.filter(x => !visibleSet.has(normalizeForCompare(x))));
  };

  return (
    <div style={{
      background: 'rgba(15,23,42,0.04)', borderRadius: 10,
      border: `1px solid ${accentBorder}`,
      padding: 12, marginBottom: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <Icone size={13} color={accentColor} />
        <span style={{ fontSize: 11.5, fontWeight: 800, color: '#334155', letterSpacing: '0.04em' }}>
          {title}
        </span>
        <span style={{
          fontSize: 10.5, color: '#334155', fontFamily: monoFont,
          marginLeft: 'auto',
          padding: '2px 8px', borderRadius: 999,
          background: accentBg, border: `1px solid ${accentBorder}`,
        }}>
          {draft.length} de {allKnown.length} selecionados
        </span>
      </div>
      {helperText && (
        <p style={{ fontSize: 10.5, color: '#64748b', marginBottom: 8 }}>{helperText}</p>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <SearchInput value={search} onChange={setSearch} placeholder="Buscar status…" />
        </div>
        <SmallBtn onClick={selectAllVisible} disabled={filtered.length === 0} variant="ghost" title="Marcar todos visíveis">
          <Check size={11} /> Marcar visíveis
        </SmallBtn>
        <SmallBtn onClick={clearAllVisible} disabled={filtered.length === 0} variant="ghost" title="Desmarcar todos visíveis">
          <X size={11} /> Limpar visíveis
        </SmallBtn>
      </div>

      <div style={{
        maxHeight: 280, overflowY: 'auto',
        border: '1px solid rgba(15,23,42,0.06)', borderRadius: 8,
        background: 'rgba(15,23,42,0.035)', padding: 8,
      }}>
        {distintosLoading && (
          <p style={{ fontSize: 11, color: '#64748b', padding: 8 }}>
            <Loader2 size={12} style={{ animation: 'spin 1s linear infinite', display: 'inline', marginRight: 6 }} />
            Carregando…
          </p>
        )}
        {!distintosLoading && allKnown.length === 0 && (
          <p style={{ fontSize: 11, color: '#64748b', padding: 8 }}>
            Nenhuma OS importada ainda para esta empresa. Use "Adicionar manual" abaixo.
          </p>
        )}
        {!distintosLoading && allKnown.length > 0 && filtered.length === 0 && (
          <p style={{ fontSize: 11, color: '#64748b', padding: 8 }}>
            Nenhum status corresponde à busca.
          </p>
        )}
        {!distintosLoading && filtered.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 4,
          }}>
            {filtered.map(v => {
              const checked = draftSet.has(normalizeForCompare(v));
              return (
                <label
                  key={v}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '6px 8px', borderRadius: 6,
                    background: checked ? accentBg : 'transparent',
                    border: `1px solid ${checked ? accentBorder : 'rgba(15,23,42,0.04)'}`,
                    cursor: 'pointer',
                    transition: 'all 0.12s',
                  }}
                  onMouseEnter={(e) => { if (!checked) e.currentTarget.style.background = 'rgba(15,23,42,0.035)'; }}
                  onMouseLeave={(e) => { if (!checked) e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{
                    width: 16, height: 16, borderRadius: 4,
                    border: `1px solid ${checked ? accentColor : 'rgba(15,23,42,0.14)'}`,
                    background: checked ? accentColor : 'transparent',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {checked && <Check size={11} color="#ffffff" strokeWidth={3} />}
                  </span>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(v)}
                    style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
                    tabIndex={-1}
                  />
                  <span
                    onClick={() => toggle(v)}
                    style={{
                      fontFamily: monoFont, fontSize: 11.5, fontWeight: 600,
                      color: checked ? accentColor : '#334155',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      flex: 1,
                    }}
                    title={v}
                  >
                    {v}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      <div style={{
        marginTop: 8, padding: 8,
        background: 'rgba(15,23,42,0.035)', borderRadius: 8,
        border: '1px dashed rgba(15,23,42,0.10)',
        display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
      }}>
        <Plus size={12} color="#64748b" />
        <span style={{ fontSize: 10.5, fontWeight: 700, color: '#334155' }}>Adicionar manual:</span>
        <input
          value={manual}
          onChange={(e) => setManual(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') addManual(); }}
          placeholder="Ex: AGUARDANDO PEÇA"
          style={{
            flex: 1, minWidth: 160, padding: '6px 10px',
            borderRadius: 8, border: '1px solid #e2e8f0',
            background: '#f8fafc', color: '#1e293b',
            fontSize: 11, outline: 'none', fontFamily: monoFont,
          }}
        />
        <SmallBtn onClick={addManual} disabled={!manual.trim()} variant="ghost">
          <Plus size={11} /> Adicionar
        </SmallBtn>
      </div>
    </div>
  );
}

function OpsAbaBlock({ empresaSlug, abaKey, abaCfg, allOps, onChanged }) {
  const ops_rule = abaCfg.ops_rule || { default_ops: [], effective_ops: [], is_overridden: false };
  const isOverridden = !!ops_rule.is_overridden;
  const [draft, setDraft] = useState(ops_rule.effective_ops || []);
  const [saving, setSaving] = useState(false);
  const [manualOp, setManualOp] = useState('');

  useEffect(() => { setDraft(ops_rule.effective_ops || []); }, [JSON.stringify(ops_rule.effective_ops)]); // eslint-disable-line

  const optsAll = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const v of [...(ops_rule.effective_ops || []), ...(ops_rule.default_ops || []), ...allOps]) {
      const k = String(v).trim();
      if (!k || seen.has(k)) continue;
      seen.add(k); out.push(k);
    }
    return out.sort((a, b) => {
      const na = parseInt(a, 10), nb = parseInt(b, 10);
      if (!isNaN(na) && !isNaN(nb)) return na - nb;
      return a.localeCompare(b);
    });
  }, [allOps, ops_rule]);

  const draftSet = new Set(draft.map(String));
  const isDirty = !setEquals(draft.map(String), (ops_rule.effective_ops || []).map(String));

  const toggle = (op) => {
    const k = String(op);
    if (draftSet.has(k)) setDraft(draft.filter(v => String(v) !== k));
    else setDraft([...draft, k]);
  };

  const addManual = () => {
    const k = manualOp.trim();
    if (!k) return;
    if (!draftSet.has(k)) setDraft([...draft, k]);
    setManualOp('');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await putAprovacaoOps(empresaSlug, abaKey, draft);
      onChanged && onChanged();
    } catch (e) {
      alert('Erro ao salvar: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const handleReset = async () => {
    if (!isOverridden) return;
    if (!window.confirm(`Resetar OPs Aceitos da aba ${abaCfg.titulo} pro default?`)) return;
    setSaving(true);
    try {
      await deleteAprovacaoOps(empresaSlug, abaKey);
      onChanged && onChanged();
    } catch (e) {
      alert('Erro ao resetar: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  return (
    <div style={{
      padding: 14, borderRadius: 10,
      background: 'rgba(15,23,42,0.04)',
      border: '1px solid rgba(15,23,42,0.08)',
      marginBottom: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>{abaCfg.titulo}</span>
        <span style={{ fontSize: 10.5, color: '#64748b', fontFamily: monoFont, marginLeft: 4 }}>
          default: {(ops_rule.default_ops || []).length ? ops_rule.default_ops.join(', ') : '(todos)'}
        </span>
        {isOverridden && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '2px 7px', borderRadius: 999,
            fontSize: 9.5, fontWeight: 800, letterSpacing: '0.1em',
            background: 'rgba(251,191,36,0.12)',
            border: '1px solid rgba(251,191,36,0.45)',
            color: '#b45309', fontFamily: monoFont, textTransform: 'uppercase',
          }}>
            <AlertTriangle size={9} />Editado
          </span>
        )}
      </div>

      {optsAll.length === 0 ? (
        <p style={{ color: '#64748b', fontSize: 11, fontStyle: 'italic' }}>
          Nenhum OP encontrado. Suba a planilha de notas para alimentar a lista.
        </p>
      ) : (
        <div style={{
          display: 'grid', gap: 6,
          gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
          padding: 8, borderRadius: 8,
          background: 'rgba(15,23,42,0.035)',
          border: '1px solid rgba(15,23,42,0.06)',
        }}>
          {optsAll.map(op => {
            const checked = draftSet.has(String(op));
            return (
              <label
                key={op}
                onClick={() => toggle(op)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '7px 9px', borderRadius: 6,
                  background: checked ? 'rgba(96,165,250,0.14)' : 'rgba(15,23,42,0.02)',
                  border: `1px solid ${checked ? 'rgba(96,165,250,0.55)' : 'rgba(15,23,42,0.08)'}`,
                  cursor: 'pointer', userSelect: 'none',
                  fontFamily: monoFont, fontSize: 11.5, fontWeight: 700,
                  color: checked ? '#1d4ed8' : '#334155',
                }}
              >
                <span style={{
                  width: 13, height: 13, borderRadius: 3,
                  background: checked ? '#60a5fa' : 'rgba(15,23,42,0.08)',
                  border: `1px solid ${checked ? '#60a5fa' : 'rgba(15,23,42,0.12)'}`,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {checked && <Check size={10} color="#ffffff" strokeWidth={3} />}
                </span>
                <span>OP {op}</span>
              </label>
            );
          })}
        </div>
      )}

      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, marginTop: 10,
        padding: '6px 8px', borderRadius: 6,
        background: 'rgba(15,23,42,0.03)',
        border: '1px solid rgba(15,23,42,0.04)',
      }}>
        <span style={{ fontSize: 10.5, color: '#64748b', fontFamily: monoFont, marginRight: 4 }}>
          Adicionar OP manual:
        </span>
        <input
          value={manualOp}
          onChange={(e) => setManualOp(e.target.value.replace(/[^0-9]/g, ''))}
          onKeyDown={(e) => { if (e.key === 'Enter') addManual(); }}
          placeholder="ex: 139"
          style={{
            flex: 1, maxWidth: 100,
            background: 'transparent', border: '1px solid rgba(15,23,42,0.10)',
            borderRadius: 4, padding: '4px 8px',
            color: '#1e293b', fontSize: 11.5, fontFamily: monoFont, outline: 'none',
          }}
        />
        <SmallBtn onClick={addManual} disabled={!manualOp.trim()} variant="ghost">
          <Plus size={10} /> Adicionar
        </SmallBtn>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
        {isOverridden && (
          <SmallBtn onClick={handleReset} variant="danger">
            <RotateCcw size={11} /> Resetar
          </SmallBtn>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 10.5, color: '#64748b', fontFamily: monoFont }}>
          {!isDirty ? 'Sem alterações' : `${draft.length} OPs marcados`}
        </span>
        <SmallBtn
          onClick={handleSave}
          disabled={!isDirty || saving}
          variant={!isDirty || saving ? 'ghost' : 'primary'}
        >
          {saving ? <Loader2 size={11} /> : <Save size={11} />}
          Salvar
        </SmallBtn>
      </div>
    </div>
  );
}

function OpsEditor({ empresaSlug, empresa, distintos, distintosLoading, onChanged }) {
  const allOps = distintos?.ops_disponiveis || [];
  const abas = empresa.abas || {};

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
        padding: '10px 12px', borderRadius: 8,
        background: 'rgba(96,165,250,0.06)',
        border: '1px solid rgba(96,165,250,0.20)',
      }}>
        <Info size={14} color="#60a5fa" />
        <span style={{ fontSize: 12, color: '#334155', flex: 1 }}>
          Filtra linhas das notas pela coluna <strong>OP</strong> — por aba. Default vazio = aceita todos os OPs daquela aba.
        </span>
      </div>

      {distintosLoading && (
        <p style={{ color: '#64748b', fontSize: 11, fontStyle: 'italic' }}>Carregando OPs disponíveis…</p>
      )}

      {Object.entries(abas).map(([abaKey, abaCfg]) => (
        <OpsAbaBlock
          key={abaKey}
          empresaSlug={empresaSlug}
          abaKey={abaKey}
          abaCfg={abaCfg}
          allOps={allOps}
          onChanged={onChanged}
        />
      ))}
    </div>
  );
}

function StatusEditor({ empresaSlug, empresa, distintos, distintosLoading, onChanged, isMobile }) {
  const sr = empresa.status_rule || {};
  const approvalField = empresa.approval_field;
  const isOverridden = !!sr.is_overridden;
  const showApproval = approvalField === 'status_encerrada';

  const initStatus = sr.effective_values || sr.default_values || [];
  const initApproval = sr.effective_approval_values || (showApproval ? sr.default_values : null) || [];
  const initMode = sr.effective_match_mode || sr.default_match_mode || 'contains';

  const [statusDraft, setStatusDraft] = useState(initStatus);
  const [approvalDraft, setApprovalDraft] = useState(initApproval);
  const [modeDraft, setModeDraft] = useState(initMode);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setStatusDraft(initStatus);
    setApprovalDraft(initApproval);
    setModeDraft(initMode);
  }, [isOverridden, initStatus.join('|'), initApproval.join('|'), initMode]); // eslint-disable-line

  const allStatus = distintos?.status || [];

  const defaultKindLabel = {
    'only_encerrada':   'Contém "ENCERRADA" (padrão)',
    'only_finalizado':  'Exato "FINALIZADO" (padrão)',
    'status_encerrada': 'Aprovação por status — contém "ENCERRADA" (VIP padrão)',
  }[sr.default_kind] || 'Sem regra de status no padrão';

  const statusDirty = !setEquals(statusDraft, initStatus);
  const approvalDirty = showApproval && !setEquals(approvalDraft, initApproval);
  const modeDirty = modeDraft !== initMode;
  const isDirty = statusDirty || approvalDirty || modeDirty;
  const canSave = isDirty && statusDraft.length > 0;

  const onSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await putAprovacaoStatus(empresaSlug, {
        status_values: statusDraft,
        approval_status_values: showApproval ? approvalDraft : null,
        match_mode: modeDraft,
      });
      await onChanged?.();
    } catch (e) {
      alert('Erro: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const onReset = async () => {
    if (!isOverridden) return;
    if (!window.confirm('Voltar para a regra padrão de status?')) return;
    setSaving(true);
    try {
      await deleteAprovacaoStatus(empresaSlug);
      await onChanged?.();
    } catch (e) {
      alert('Erro: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  return (
    <div style={{
      borderRadius: 12,
      border: isOverridden
        ? '1px solid rgba(251,191,36,0.30)'
        : '1px solid rgba(251,191,36,0.18)',
      background: 'linear-gradient(180deg, rgba(251,191,36,0.04) 0%, rgba(255,255,255,0.7) 100%)',
      padding: 14,
    }}>
      <div style={{
        background: 'rgba(96,165,250,0.06)',
        border: '1px solid rgba(96,165,250,0.20)',
        borderRadius: 10, padding: '10px 12px', marginBottom: 12,
        display: 'flex', gap: 10, alignItems: 'flex-start',
      }}>
        <Info size={14} color="#93c5fd" style={{ marginTop: 2, flexShrink: 0 }} />
        <div style={{ fontSize: 11.5, color: '#334155', lineHeight: 1.5, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <strong style={{ color: '#1d4ed8' }}>Regra default do sistema:</strong>
            <span>{defaultKindLabel}</span>
            {isOverridden && <EditedBadge />}
          </div>
          {approvalField === 'os_aprovada' && (
            <p style={{ marginTop: 4, color: '#b45309' }}>
              Esta empresa aprova OS pela coluna <code style={{ fontFamily: monoFont }}>OS Aprovada</code>, então status só decide se a OS <em>entra na conta</em>, não se está aprovada.
            </p>
          )}
          {approvalField === 'status_encerrada' && (
            <p style={{ marginTop: 4, color: '#047857' }}>
              Esta empresa aprova OS pelo status. Os "Status que ATIVAM aprovação" são checados.
            </p>
          )}
          {sr.updated_by && (
            <p style={{ marginTop: 6, fontSize: 10, color: '#64748b', fontFamily: monoFont }}>
              Última edição: <span style={{ color: '#334155' }}>{sr.updated_by}</span>
              {sr.updated_at && ` em ${new Date(sr.updated_at).toLocaleString('pt-BR')}`}
            </p>
          )}
        </div>
      </div>

      {/* Match mode */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
        padding: '10px 12px', borderRadius: 10,
        background: 'rgba(15,23,42,0.035)', border: '1px solid rgba(15,23,42,0.06)',
        flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          Modo de comparação:
        </span>
        {['contains', 'exact'].map(m => (
          <button
            key={m}
            onClick={() => setModeDraft(m)}
            style={{
              padding: '6px 12px', borderRadius: 999,
              background: modeDraft === m ? 'rgba(251,191,36,0.18)' : 'rgba(15,23,42,0.035)',
              border: `1px solid ${modeDraft === m ? 'rgba(251,191,36,0.45)' : 'rgba(15,23,42,0.08)'}`,
              color: modeDraft === m ? '#b45309' : '#475569',
              fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
              fontFamily: monoFont,
            }}
            title={m === 'contains' ? 'Substring (uppercase)' : 'Igualdade exata (uppercase)'}
          >
            {m === 'contains' ? 'Contém' : 'Exato'}
          </button>
        ))}
        <span style={{ fontSize: 10.5, color: '#64748b', marginLeft: 'auto' }}>
          {modeDraft === 'contains' ? 'cada valor é substring do status' : 'igualdade absoluta'}
        </span>
      </div>

      <StatusCheckboxGrid
        title="Status que CONTAM na conta"
        Icone={Check}
        accentColor="#15803d"
        accentBg="rgba(52,211,153,0.10)"
        accentBorder="rgba(52,211,153,0.30)"
        allStatus={allStatus}
        distintosLoading={distintosLoading}
        draft={statusDraft}
        setDraft={setStatusDraft}
        helperText="OS com status fora dessa lista são ignoradas no cálculo."
      />

      {showApproval && (
        <StatusCheckboxGrid
          title="Status que ATIVAM aprovação"
          Icone={Activity}
          accentColor="#047857"
          accentBg="rgba(52,211,153,0.12)"
          accentBorder="rgba(52,211,153,0.35)"
          allStatus={allStatus}
          distintosLoading={distintosLoading}
          draft={approvalDraft}
          setDraft={setApprovalDraft}
          helperText="Independente do valor: se o status estiver aqui (e a OS entrar na conta), conta como aprovada."
        />
      )}

      {/* Footer */}
      <div style={{
        marginTop: 4, paddingTop: 10,
        borderTop: '1px solid rgba(15,23,42,0.08)',
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
      }}>
        {isOverridden && (
          <SmallBtn onClick={onReset} variant="outline" disabled={saving} title="Voltar ao padrão do sistema">
            <RotateCcw size={12} /> Resetar para padrão
          </SmallBtn>
        )}
        <span style={{ fontSize: 10.5, color: '#64748b', fontFamily: monoFont, marginLeft: 'auto' }}>
          {!isDirty && 'Sem alterações'}
          {isDirty && statusDraft.length === 0 && 'Marque ao menos 1 status'}
          {isDirty && statusDraft.length > 0 && 'Alterações pendentes'}
        </span>
        <SmallBtn
          onClick={onSave}
          disabled={!canSave || saving}
          variant="solid"
          title={statusDraft.length === 0 ? 'Marque ao menos 1 status' : 'Salvar alterações'}
        >
          {saving ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={12} />}
          Salvar
        </SmallBtn>
      </div>
    </div>
  );
}

// =================== REGRAS EDITOR ===================

function RegraRow({ empresaSlug, abaKey, abaConfig, onChanged, isMobile }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [thr, setThr] = useState(String(abaConfig.threshold ?? 0));
  const [cmp, setCmp] = useState(abaConfig.comparison || 'gte');
  const [minV, setMinV] = useState(String(abaConfig.min_value ?? 0));
  const [metaApr, setMetaApr] = useState(String(abaConfig.meta_aprovacao ?? 0));
  const [metaRec, setMetaRec] = useState(String(abaConfig.meta_receita ?? 0));

  const isOverridden = !!abaConfig.threshold_is_overridden;

  useEffect(() => {
    setThr(String(abaConfig.threshold ?? 0));
    setCmp(abaConfig.comparison || 'gte');
    setMinV(String(abaConfig.min_value ?? 0));
    setMetaApr(String(abaConfig.meta_aprovacao ?? 0));
    setMetaRec(String(abaConfig.meta_receita ?? 0));
  }, [abaConfig.threshold, abaConfig.comparison, abaConfig.min_value, abaConfig.meta_aprovacao, abaConfig.meta_receita]);

  const onSave = async () => {
    const t = parseFloat(String(thr).replace(',', '.'));
    const m = parseFloat(String(minV).replace(',', '.'));
    const ma = parseFloat(String(metaApr).replace(',', '.'));
    const mr = parseFloat(String(metaRec).replace(',', '.'));
    if (!isFinite(t) || t < 0) { alert('Threshold inválido'); return; }
    if (!isFinite(ma) || ma < 0 || ma > 100) { alert('Meta aprovação inválida (0-100)'); return; }
    if (!isFinite(mr) || mr < 0) { alert('Meta receita inválida'); return; }
    setSaving(true);
    try {
      await setAprovacaoThreshold(
        empresaSlug, abaKey,
        t, cmp, 'admin',
        isFinite(m) && m >= 0 ? m : 0,
        ma, mr,
      );
      setEditing(false);
      await onChanged?.();
    } catch (e) {
      alert('Erro: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const onReset = async () => {
    if (!isOverridden) return;
    if (!window.confirm('Voltar para os valores padrão dessa aba?')) return;
    setSaving(true);
    try {
      await deleteAprovacaoThreshold(empresaSlug, abaKey);
      setEditing(false);
      await onChanged?.();
    } catch (e) {
      alert('Erro: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const cellStyle = {
    fontFamily: monoFont, fontSize: 12, color: '#1e293b',
    padding: '8px 6px', textAlign: 'right',
  };
  const inputStyle = {
    width: '100%', padding: '6px 8px', borderRadius: 6,
    border: '1px solid #cbd5e1', background: '#f8fafc',
    color: '#0f172a', fontSize: 11.5, outline: 'none',
    fontFamily: monoFont, textAlign: 'right',
  };

  if (isMobile) {
    return (
      <div style={{
        background: 'rgba(15,23,42,0.035)', borderRadius: 10,
        border: `1px solid ${isOverridden ? 'rgba(251,191,36,0.30)' : 'rgba(15,23,42,0.08)'}`,
        padding: 12, marginBottom: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <span style={{ fontSize: 12.5, fontWeight: 800, color: '#0f172a', flex: 1 }}>{abaConfig.titulo}</span>
          {isOverridden && <EditedBadge />}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Field label="Threshold" value={thr} onChange={setThr} editing={editing} fmt={v => `R$ ${v}`} />
          <Field label="Mín. valor" value={minV} onChange={setMinV} editing={editing} fmt={v => `R$ ${v}`} />
          <Field label="Meta aprov. (%)" value={metaApr} onChange={setMetaApr} editing={editing} fmt={v => `${v}%`} />
          <Field label="Meta receita" value={metaRec} onChange={setMetaRec} editing={editing} fmt={v => `R$ ${v}`} />
        </div>
        <div style={{ marginTop: 10 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#475569', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Comparação:</span>
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            {['gte', 'gt'].map(c => (
              <button
                key={c}
                onClick={() => editing && setCmp(c)}
                disabled={!editing}
                style={{
                  flex: 1, padding: '6px', borderRadius: 6,
                  background: cmp === c ? 'rgba(96,165,250,0.18)' : 'rgba(15,23,42,0.035)',
                  border: `1px solid ${cmp === c ? 'rgba(96,165,250,0.45)' : 'rgba(15,23,42,0.08)'}`,
                  color: cmp === c ? '#1d4ed8' : '#475569', fontFamily: monoFont,
                  fontSize: 11, fontWeight: 700, cursor: editing ? 'pointer' : 'default',
                }}
              >
                {c === 'gte' ? '≥' : '>'}
              </button>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
          {editing ? (
            <>
              <SmallBtn onClick={() => setEditing(false)} variant="ghost">Cancelar</SmallBtn>
              {isOverridden && <SmallBtn onClick={onReset} variant="outline" disabled={saving}><RotateCcw size={12} /></SmallBtn>}
              <SmallBtn onClick={onSave} disabled={saving} variant="solid"><Save size={12} /> Salvar</SmallBtn>
            </>
          ) : (
            <SmallBtn onClick={() => setEditing(true)} variant="ghost">Editar</SmallBtn>
          )}
        </div>
      </div>
    );
  }

  return (
    <tr style={{
      background: isOverridden ? 'rgba(251,191,36,0.05)' : 'transparent',
      borderBottom: '1px solid rgba(15,23,42,0.04)',
    }}>
      <td style={{ padding: '10px 10px', fontSize: 12.5, fontWeight: 700, color: '#1e293b' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>{abaConfig.titulo}</span>
          {isOverridden && <EditedBadge />}
        </div>
        <div style={{ fontSize: 10, color: '#64748b', fontFamily: monoFont, marginTop: 2 }}>{abaKey}</div>
      </td>
      <td style={cellStyle}>
        {editing ? <input value={thr} onChange={e => setThr(e.target.value)} style={inputStyle} />
          : <>R$ {thr} <span style={{ color: '#64748b' }}>· default: R$ {abaConfig.default_threshold ?? '—'}</span></>}
      </td>
      <td style={cellStyle}>
        {editing ? (
          <select value={cmp} onChange={e => setCmp(e.target.value)} style={inputStyle}>
            <option value="gte">≥</option>
            <option value="gt">&gt;</option>
          </select>
        ) : (cmp === 'gte' ? '≥' : '>')}
      </td>
      <td style={cellStyle}>
        {editing ? <input value={minV} onChange={e => setMinV(e.target.value)} style={inputStyle} />
          : <>R$ {minV}</>}
      </td>
      <td style={cellStyle}>
        {editing ? <input value={metaApr} onChange={e => setMetaApr(e.target.value)} style={inputStyle} />
          : <>{metaApr}%</>}
      </td>
      <td style={cellStyle}>
        {editing ? <input value={metaRec} onChange={e => setMetaRec(e.target.value)} style={inputStyle} />
          : <>R$ {metaRec}</>}
      </td>
      <td style={{ padding: '6px', textAlign: 'right', whiteSpace: 'nowrap' }}>
        {editing ? (
          <>
            <SmallBtn onClick={() => setEditing(false)} variant="ghost">Cancelar</SmallBtn>
            <span style={{ display: 'inline-block', width: 6 }} />
            <SmallBtn onClick={onSave} disabled={saving} variant="solid">
              {saving ? <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={11} />}
            </SmallBtn>
          </>
        ) : (
          <>
            {isOverridden && (
              <>
                <SmallBtn onClick={onReset} disabled={saving} variant="outline" title="Resetar para padrão"><RotateCcw size={11} /></SmallBtn>
                <span style={{ display: 'inline-block', width: 6 }} />
              </>
            )}
            <SmallBtn onClick={() => setEditing(true)} variant="ghost">Editar</SmallBtn>
          </>
        )}
      </td>
    </tr>
  );
}

function Field({ label, value, onChange, editing, fmt }) {
  return (
    <div style={{
      padding: 8, borderRadius: 8,
      background: 'rgba(15,23,42,0.035)', border: '1px solid rgba(15,23,42,0.04)',
    }}>
      <div style={{ fontSize: 9.5, fontWeight: 700, color: '#64748b', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>
        {label}
      </div>
      {editing ? (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{
            width: '100%', padding: '6px 8px', borderRadius: 6,
            border: '1px solid #cbd5e1', background: '#f8fafc',
            color: '#0f172a', fontSize: 12, outline: 'none',
            fontFamily: monoFont,
          }}
        />
      ) : (
        <div style={{ fontFamily: monoFont, fontSize: 13, color: '#1e293b', fontWeight: 700 }}>{fmt ? fmt(value) : value}</div>
      )}
    </div>
  );
}

function RegrasEditor({ empresaSlug, empresa, onChanged, isMobile }) {
  const abas = empresa.abas || {};
  const abaKeys = Object.keys(abas);

  if (abaKeys.length === 0) {
    return <p style={{ fontSize: 11.5, color: '#64748b', padding: 20, textAlign: 'center' }}>Sem abas configuradas para esta empresa.</p>;
  }

  if (isMobile) {
    return (
      <div>
        {abaKeys.map(k => (
          <RegraRow
            key={k}
            empresaSlug={empresaSlug}
            abaKey={k}
            abaConfig={abas[k]}
            onChanged={onChanged}
            isMobile
          />
        ))}
      </div>
    );
  }

  const th = {
    padding: '8px 6px', textAlign: 'right',
    fontSize: 10, fontWeight: 800, color: '#64748b',
    letterSpacing: '0.06em', textTransform: 'uppercase',
    borderBottom: '1px solid #e2e8f0',
  };
  return (
    <div style={{
      borderRadius: 12,
      border: '1px solid rgba(96,165,250,0.18)',
      background: 'linear-gradient(180deg, rgba(96,165,250,0.04) 0%, rgba(255,255,255,0.7) 100%)',
      padding: 4,
      overflowX: 'auto',
    }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
        <thead>
          <tr>
            <th style={{ ...th, textAlign: 'left', paddingLeft: 12 }}>Aba</th>
            <th style={th}>Threshold</th>
            <th style={th}>Comp.</th>
            <th style={th}>Mín. valor</th>
            <th style={th}>Meta aprov.</th>
            <th style={th}>Meta receita</th>
            <th style={th}>&nbsp;</th>
          </tr>
        </thead>
        <tbody>
          {abaKeys.map(k => (
            <RegraRow
              key={k}
              empresaSlug={empresaSlug}
              abaKey={k}
              abaConfig={abas[k]}
              onChanged={onChanged}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// =================== MAIN MODAL ===================

export default function MapeamentoModal({
  initialEmpresa = null,
  companies = [],
  onClose,
  onSaved,
}) {
  const [mapping, setMapping] = useState(null);
  const [loadingMapping, setLoadingMapping] = useState(true);
  const [activeSlug, setActiveSlug] = useState(initialEmpresa || null);
  const [activeSection, setActiveSection] = useState('filtros');
  const [distintosCache, setDistintosCache] = useState({});
  const [distintosLoading, setDistintosLoading] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  const [resetting, setResetting] = useState(false);
  const justSavedRef = useRef(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 720);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const refreshMapping = useCallback(async () => {
    setLoadingMapping(true);
    try {
      const r = await getAprovacoesMapping();
      setMapping(r.data);
      if (!activeSlug) {
        const first = Object.keys(r.data?.empresas || {})[0];
        if (first) setActiveSlug(first);
      }
    } catch (e) {
      setMapping(null);
    }
    setLoadingMapping(false);
  }, [activeSlug]);

  useEffect(() => { refreshMapping(); }, []); // eslint-disable-line

  const loadDistintos = useCallback(async (slug) => {
    if (distintosCache[slug]) return;
    const company = companies.find(c => c.slug === slug);
    if (!company) return;
    setDistintosLoading(prev => ({ ...prev, [slug]: true }));
    try {
      const r = await getAprovacoesMappingDistintos(slug, company.ascCode);
      setDistintosCache(prev => ({ ...prev, [slug]: r.data }));
    } catch {
      setDistintosCache(prev => ({ ...prev, [slug]: { tipo_servico: [], aparelho: [], status: [] } }));
    }
    setDistintosLoading(prev => ({ ...prev, [slug]: false }));
  }, [companies, distintosCache]);

  useEffect(() => {
    if (activeSlug) loadDistintos(activeSlug);
  }, [activeSlug, loadDistintos]);

  const handleSaved = useCallback(async () => {
    justSavedRef.current = true;
    await refreshMapping();
    if (onSaved) await onSaved();
    setTimeout(() => { justSavedRef.current = false; }, 1500);
  }, [refreshMapping, onSaved]);

  const resetEmpresaAll = async () => {
    if (!activeSlug) return;
    if (!window.confirm('Resetar TODOS os overrides (filtros, status e thresholds) desta empresa?')) return;
    const confirm2 = window.prompt('Esta ação é destrutiva. Digite "RESETAR" para confirmar:');
    if (confirm2 !== 'RESETAR') return;
    setResetting(true);
    try {
      await resetAprovacaoMapping(activeSlug, 'all');
      await handleSaved();
    } catch (e) {
      alert('Erro: ' + (e.response?.data?.detail || e.message));
    }
    setResetting(false);
  };

  const empresas = mapping?.empresas || {};
  const empresasList = Object.entries(empresas);
  const active = activeSlug && empresas[activeSlug];

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 110,
      background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'center',
      padding: isMobile ? 0 : 20,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'linear-gradient(180deg, #ffffff 0%, #f1f5f9 100%)',
        borderRadius: isMobile ? 0 : 18, border: isMobile ? 'none' : '1px solid #e2e8f0',
        maxWidth: 1080, width: '100%', maxHeight: isMobile ? '100%' : '92vh',
        height: isMobile ? '100%' : 'auto',
        overflow: 'hidden', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(15,23,42,0.10)',
      }}>
        {/* Header */}
        <div style={{
          padding: isMobile ? '14px 16px' : '18px 22px',
          display: 'flex', alignItems: 'center', gap: 14,
          borderBottom: '1px solid #e2e8f0',
          background: 'linear-gradient(180deg, rgba(244,114,182,0.08) 0%, transparent 100%)',
          flexWrap: 'wrap',
        }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: 'linear-gradient(135deg, rgba(244,114,182,0.28) 0%, rgba(244,114,182,0.08) 100%)',
            border: '1px solid rgba(244,114,182,0.40)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Tag size={20} color="#f9a8d4" strokeWidth={2.2} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ fontSize: 17, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.01em' }}>Mapeamento de Aprovações</h2>
            <p style={{ fontSize: 11.5, color: '#475569', marginTop: 2 }}>
              Editar filtros, status e regras numéricas por loja · alterações são globais
            </p>
          </div>
          {justSavedRef.current && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '5px 12px', borderRadius: 999,
              background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.35)',
              color: '#15803d', fontSize: 11, fontWeight: 800,
              fontFamily: monoFont, letterSpacing: '0.05em',
            }}>
              <Check size={12} /> SALVO
            </span>
          )}
          <button onClick={onClose} aria-label="Fechar" style={{
            background: 'rgba(15,23,42,0.06)', border: '1px solid rgba(15,23,42,0.08)',
            borderRadius: 10, padding: 8, cursor: 'pointer', color: '#334155',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.15s',
          }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(15,23,42,0.10)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(15,23,42,0.06)'; }}
          ><X size={18} /></button>
        </div>

        {/* Empresa tabs */}
        <div style={{
          padding: isMobile ? '10px 16px' : '12px 22px',
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          borderBottom: '1px solid #e2e8f0',
        }}>
          {isMobile ? (
            <select
              value={activeSlug || ''}
              onChange={(e) => setActiveSlug(e.target.value)}
              style={{
                flex: 1, padding: '10px 12px', borderRadius: 10,
                border: '1px solid #cbd5e1', background: '#f8fafc',
                color: '#0f172a', fontSize: 13, fontWeight: 700, outline: 'none',
              }}
            >
              {empresasList.map(([slug, emp]) => (
                <option key={slug} value={slug}>{emp.nome}</option>
              ))}
            </select>
          ) : (
            empresasList.map(([slug, emp]) => {
              const theme = COMPANY_THEME[slug] || { color: '#475569' };
              const isActive = slug === activeSlug;
              const anyEdit = !!emp.status_rule?.is_overridden ||
                Object.values(emp.abas || {}).some(a => a.filtros_is_overridden || a.threshold_is_overridden);
              return (
                <button
                  key={slug}
                  onClick={() => setActiveSlug(slug)}
                  style={{
                    padding: '8px 14px', borderRadius: 10,
                    background: isActive ? `${theme.color}18` : 'rgba(15,23,42,0.035)',
                    border: `1px solid ${isActive ? theme.color + '55' : 'rgba(15,23,42,0.08)'}`,
                    color: isActive ? '#0f172a' : '#334155',
                    cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    fontSize: 12.5, fontWeight: 700, letterSpacing: '0.02em',
                    transition: 'all 0.15s',
                  }}
                >
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: theme.color, boxShadow: isActive ? `0 0 10px ${theme.color}` : 'none',
                  }} />
                  {emp.nome}
                  {anyEdit && (
                    <span style={{
                      width: 6, height: 6, borderRadius: '50%',
                      background: '#fbbf24', boxShadow: '0 0 6px #fbbf2488',
                    }} />
                  )}
                </button>
              );
            })
          )}
        </div>

        {/* Section tabs */}
        {active && (
          <div style={{
            padding: '10px 22px',
            display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
            borderBottom: '1px solid #e2e8f0',
            background: 'rgba(15,23,42,0.03)',
          }}>
            {[
              { key: 'filtros', label: 'Filtros por Aba', Icone: Filter },
              { key: 'status', label: 'Status Considerados', Icone: Activity },
              { key: 'ops', label: 'OPs Aceitos', Icone: Tag },
              { key: 'regras', label: 'Regras Numéricas', Icone: Sliders },
            ].map(s => {
              const isAct = s.key === activeSection;
              const col = SECTION_COLOR[s.key];
              return (
                <button
                  key={s.key}
                  onClick={() => setActiveSection(s.key)}
                  style={{
                    padding: '7px 13px', borderRadius: 8,
                    background: isAct ? `${col}18` : 'transparent',
                    border: `1px solid ${isAct ? col + '40' : 'transparent'}`,
                    color: isAct ? '#0f172a' : '#475569',
                    cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    fontSize: 11.5, fontWeight: 700, letterSpacing: '0.02em',
                    transition: 'all 0.15s',
                  }}
                >
                  <s.Icone size={12} color={isAct ? col : '#475569'} />
                  {s.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Body */}
        <div style={{
          overflowY: 'auto',
          padding: isMobile ? '14px 16px' : '18px 22px',
          flex: 1,
        }}>
          {loadingMapping && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              padding: 60, color: '#475569',
            }}>
              <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ fontSize: 13, fontWeight: 600 }}>Carregando mapeamento…</span>
            </div>
          )}
          {!loadingMapping && !active && (
            <p style={{ fontSize: 12.5, color: '#64748b', textAlign: 'center', padding: 40 }}>
              Selecione uma empresa acima.
            </p>
          )}
          {!loadingMapping && active && (
            <>
              {/* Empresa summary */}
              <div style={{
                background: 'rgba(15,23,42,0.035)', borderRadius: 12,
                border: '1px solid rgba(15,23,42,0.06)',
                padding: '12px 14px', marginBottom: 16,
                display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center',
              }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>{active.nome_completo}</span>
                <Pill color="#1d4ed8">filter_field: {active.filter_field}</Pill>
                <Pill color="#b45309">match: {active.match_type === 'exact' ? 'exato' : 'contém'}</Pill>
                {active.approval_field && <Pill color="#6d28d9">approval: {active.approval_field}</Pill>}
                {!active.approval_field && <Pill color="#64748b">approval: somente valor</Pill>}
              </div>

              {activeSection === 'filtros' && (
                <div>
                  {Object.entries(active.abas || {}).map(([abaKey, abaCfg]) => (
                    <FiltrosEditor
                      key={abaKey}
                      empresaSlug={activeSlug}
                      abaKey={abaKey}
                      abaConfig={abaCfg}
                      filterField={active.filter_field}
                      matchType={active.match_type}
                      distintos={distintosCache[activeSlug]}
                      distintosLoading={!!distintosLoading[activeSlug]}
                      onChanged={handleSaved}
                      isMobile={isMobile}
                    />
                  ))}
                </div>
              )}

              {activeSection === 'status' && (
                <StatusEditor
                  empresaSlug={activeSlug}
                  empresa={active}
                  distintos={distintosCache[activeSlug]}
                  distintosLoading={!!distintosLoading[activeSlug]}
                  onChanged={handleSaved}
                  isMobile={isMobile}
                />
              )}

              {activeSection === 'ops' && (
                <OpsEditor
                  empresaSlug={activeSlug}
                  empresa={active}
                  distintos={distintosCache[activeSlug]}
                  distintosLoading={!!distintosLoading[activeSlug]}
                  onChanged={handleSaved}
                />
              )}

              {activeSection === 'regras' && (
                <RegrasEditor
                  empresaSlug={activeSlug}
                  empresa={active}
                  onChanged={handleSaved}
                  isMobile={isMobile}
                />
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 22px',
          borderTop: '1px solid #e2e8f0',
          background: 'linear-gradient(0deg, rgba(15,23,42,0.04), transparent)',
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        }}>
          {active && (
            <SmallBtn onClick={resetEmpresaAll} disabled={resetting} variant="outline" danger>
              {resetting ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Trash2 size={12} />}
              Resetar TUDO desta empresa
            </SmallBtn>
          )}
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 10, color: '#64748b', fontFamily: monoFont }}>
            Cada seção salva independentemente
          </span>
          <SmallBtn onClick={onClose} variant="ghost">Fechar</SmallBtn>
        </div>
      </div>
    </div>
  );
}
